/**
 * COACHDIA Pulse — Dashboard d'équipe PREMIUM FULL (Iter268 P2)
 * ==============================================================
 *
 * Dashboard niveau cabinet de conseil — outil de pilotage premium.
 *
 * Routes :
 *   /team-pulse/teams             → liste des équipes (hero + cards)
 *   /team-pulse/teams/:teamId     → détail équipe (hero + sidebar + 5 sections)
 *
 * Structure détail :
 *   ┌────────── HERO ESPRESSO (3 sec de lecture stratégique) ─────────┐
 *   │ Verdict MAJUSCULES · Balance · Dominante · CTAs Gold            │
 *   └──────────────────────────────────────────────────────────────────┘
 *   ┌─ SIDEBAR sticky ──────┬─── MAIN ───────────────────────────────┐
 *   │ I   Santé actuelle    │  I   Santé (KPIs + distribution)       │
 *   │ II  Roster            │  II  Roster premium (rôles implicites) │
 *   │ III Tester intégration│  III Newcomer quick-probe CENTRAL      │
 *   │ IV  Évolution         │  IV  Timeline waves (SVG premium)      │
 *   │ V   Historique        │  V   Historique détaillé               │
 *   └───────────────────────┴────────────────────────────────────────┘
 *
 * Palette : #1F1A12 espresso · #92710A / #F3D060 or · #F5F1EA cream · #6B5E48 stone.
 * Typo : Georgia serif sur h1/h2 + drop-cap éditorial, tabular-nums sur chiffres.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Plus, ArrowRight, Activity, Clock,
  TrendingUp, History, Loader2, ShieldCheck,
  UserPlus, Target, FileText, Download, GitCompare, CircleCheck,
  Sparkles, Eye, Mic, ChevronDown, Lock,
} from 'lucide-react';
import { getApiUrl } from '../config/environment';
import BackButton from '../components/BackButton';
import AddMemberFlow from '../components/pulse/AddMemberFlow';
import RefreshWaveFlow from '../components/pulse/RefreshWaveFlow';
import RosterEditor from '../components/pulse/RosterEditor';
import CompareReportsModal from '../components/pulse/CompareReportsModal';

const API = getApiUrl();
const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/* ---------- Design tokens ---------- */
const COLOR_TOKENS = {
  red:    { label: 'Action',    hex: '#B94A48', soft: '#F5E0DE' },
  yellow: { label: 'Influence', hex: '#C8A64B', soft: '#F5ECCE' },
  green:  { label: 'Harmonie',  hex: '#5B8A6B', soft: '#DDE8DF' },
  blue:   { label: 'Analyse',   hex: '#3B6B8F', soft: '#D7E4EE' },
};
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch { return ''; }
};
const fmtShort = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch { return ''; }
};

/* ---------- Iter293 — Section pliable PREMIUM ---------- */
/**
 * État mémorisé via localStorage pour persistance inter-sessions.
 * Animation : grid-template-rows pour transition fluide (200ms).
 * Mobile-first : tap-target large, chevron rotation, accessible.
 */
const CollapsibleSection = ({
  id, sectionLabel, title, defaultOpen = false, children, dataTestId,
}) => {
  const storageKey = `pulse-collapse:${id}`;
  const [open, setOpen] = useState(() => {
    if (typeof window === 'undefined') return defaultOpen;
    const stored = window.localStorage.getItem(storageKey);
    return stored === null ? defaultOpen : stored === 'true';
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { window.localStorage.setItem(storageKey, String(next)); } catch { /* noop */ }
  };
  return (
    <section id={`pulse-section-${id}`} data-testid={dataTestId} className="scroll-mt-6">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={`pulse-collapse-body-${id}`}
        className="w-full flex items-center justify-between gap-3 text-left group cursor-pointer pb-3 border-b border-[#E5DFD0] hover:border-[#92710A]/40 transition-colors"
        data-testid={`${dataTestId}-toggle`}
      >
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] font-semibold mb-1">{sectionLabel}</div>
          <h2 className="font-serif text-3xl truncate">{title}</h2>
        </div>
        <span
          className={`shrink-0 w-10 h-10 rounded-full bg-[#FBF8F1] border border-[#E5DFD0] flex items-center justify-center text-[#92710A] group-hover:bg-[#F3D060]/20 group-hover:border-[#92710A]/40 transition-all ${open ? 'rotate-180' : ''}`}
          aria-hidden
        >
          <ChevronDown className="w-5 h-5" />
        </span>
      </button>
      <div
        id={`pulse-collapse-body-${id}`}
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? 'grid-rows-[1fr] opacity-100 mt-5' : 'grid-rows-[0fr] opacity-0 mt-0'}`}
        aria-hidden={!open}
      >
        <div className="overflow-hidden">
          {children}
        </div>
      </div>
    </section>
  );
};

/* ---------- Monogram with dominant-color ring ---------- */
const Monogram = ({ name, email, color = '#92710A', size = 56, ring = null }) => {
  const initials = (name || email || '?')
    .split(/\s+|@|\./).filter(Boolean).slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '').join('') || '?';
  return (
    <div
      className="flex items-center justify-center rounded-full font-serif text-white relative"
      style={{
        width: size, height: size, background: color, fontSize: size * 0.36,
        letterSpacing: '1px',
        boxShadow: ring ? `0 0 0 3px ${ring}` : undefined,
      }}
      data-testid="pulse-monogram"
    >
      {initials}
    </div>
  );
};

/* =============================================================================
 * LIST VIEW — /team-pulse/teams
 * =============================================================================*/
export function PulseTeamsList() {
  const navigate = useNavigate();
  const [teams, setTeams] = useState(null);      // null = pas encore chargé
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/team-pulse/teams`, { headers: authHeaders() });
        if (mounted) setTeams(r.data.teams || []);
      } catch {
        if (mounted) { toast.error('Impossible de charger les équipes.'); setTeams([]); }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // 1-click UX : si une seule équipe, rendu déclaratif <Navigate> (pas setState pendant render)
  if (!loading && teams && teams.length === 1) {
    return <Navigate to={`/team-pulse/teams/${teams[0].id}`} replace />;
  }

  // Iter277 P0 — éviter le flicker du hero sombre pendant le chargement
  // Quand l'user n'a qu'une équipe, on redirige juste après ce rendu transparent
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F1EA] flex items-center justify-center" data-testid="pulse-teams-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#92710A]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#FAFAF8', color: '#1C1147' }}>
      <section className="relative overflow-hidden py-16 px-6 lg:px-12" style={{ background: '#FAFAF8' }}>
        {/* Halos ambient or + violet */}
        <span aria-hidden className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0) 70%)', filter: 'blur(40px)' }} />
        <span aria-hidden className="absolute top-[40%] -left-32 w-[420px] h-[420px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(122,79,207,0.10) 0%, rgba(122,79,207,0) 70%)', filter: 'blur(40px)' }} />
        <div className="max-w-6xl mx-auto relative">
          <BackButton variant="default" data-testid="pulse-teams-back" />
          <div className="flex items-center gap-2 mt-4 mb-2">
            <span className="w-6 h-px" style={{ background: 'linear-gradient(90deg, transparent, #D4AF37)' }} />
            <div className="text-[11px] tracking-[0.22em] uppercase font-semibold" style={{ color: '#B7901F' }}>
              CoachDIA · Pulse · Équipes
            </div>
          </div>
          <h1
            className="font-serif text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            style={{
              background: 'linear-gradient(135deg, #1C1147 0%, #3A2E6E 60%, #B7901F 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontWeight: 600,
              letterSpacing: '-0.02em',
            }}
          >
            Vos équipes{' '}
            <span
              className="italic"
              style={{
                background: 'linear-gradient(135deg, #B7901F 0%, #D4AF37 50%, #F3D060 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              en vie.
            </span>
          </h1>
          <p className="mt-5 text-lg font-light max-w-2xl italic leading-relaxed" style={{ color: '#5A5169' }}>
            « Un dashboard pour piloter la dynamique collective, accueillir un nouveau
            membre, mesurer l'évolution — avec la rigueur d'un cabinet de conseil. »
          </p>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-6 lg:px-12 py-12">
        {loading ? (
          <div className="flex items-center gap-3 text-[#6B5E48]">
            <Loader2 className="w-5 h-5 animate-spin" /> Chargement des équipes...
          </div>
        ) : teams.length === 0 ? (
          <div className="bg-white border border-[#E5DFD0] rounded-2xl p-12 text-center max-w-2xl mx-auto" data-testid="pulse-teams-empty">
            <Users className="w-12 h-12 text-[#92710A] mx-auto mb-4" />
            <h2 className="font-serif text-2xl mb-3">Pas encore d'équipe mesurée.</h2>
            <p className="text-[#6B5E48] mb-6 leading-relaxed">
              Lancez votre première campagne Pulse pour voir apparaître votre équipe ici —
              puis simulez l'intégration de nouveaux membres avant même de les recruter.
            </p>
            <button
              onClick={() => navigate('/team-pulse')}
              className="px-7 py-3 rounded-full transition inline-flex items-center gap-2 font-semibold"
              style={{
                background: 'linear-gradient(135deg, #B7901F 0%, #D4AF37 50%, #F3D060 100%)',
                color: '#1C1147',
                border: '1px solid rgba(255,255,255,0.4)',
                boxShadow: '0 1px 0 rgba(255,255,255,0.4) inset, 0 12px 28px -10px rgba(212,175,55,0.55)',
              }}
              data-testid="pulse-teams-create-first"
            >
              <Plus className="w-4 h-4" /> Lancer / Suivi mesure
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5" data-testid="pulse-teams-list">
            {teams.map((t) => (
              <button
                key={t.id}
                onClick={() => navigate(`/team-pulse/teams/${t.id}`)}
                className="group bg-white border border-[#E5DFD0] rounded-2xl p-6 text-left hover:border-[#92710A] hover:shadow-[0_10px_30px_-10px_rgba(31,26,18,0.2)] transition-all"
                data-testid={`pulse-team-card-${t.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <Monogram name={t.name} size={48} />
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] font-semibold">
                    Vague {t.waves_count}
                  </div>
                </div>
                <h3 className="font-serif text-xl mb-1">{t.name}</h3>
                <p className="text-sm text-[#6B5E48]">
                  {(t.core_roster || []).length} membres · Mise à jour {fmtDate(t.updated_at)}
                </p>
                <div className="mt-4 flex items-center text-[#92710A] text-sm font-medium group-hover:gap-2 transition-all">
                  Ouvrir le dashboard <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

/* =============================================================================
 * DETAIL VIEW — /team-pulse/teams/:teamId
 * =============================================================================*/
const SECTIONS = [
  { id: 'health',      label: 'Santé actuelle', icon: Activity },
  { id: 'add-member',  label: 'Ajouter un membre', icon: UserPlus },
  { id: 'reports',     label: 'Rapports',       icon: FileText },
  { id: 'evolution',   label: 'Évolution',      icon: TrendingUp },
  { id: 'history',     label: 'Historique',     icon: History },
];

export function PulseTeamDetail() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('health');
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [refreshOpen, setRefreshOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(null);  // { currentBatch, baselineBatch }
  const [regeneratingId, setRegeneratingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/team-pulse/teams/${teamId}`, { headers: authHeaders() });
      setData(r.data);
    } catch {
      toast.error('Équipe introuvable.');
      navigate('/team-pulse/teams');
    } finally {
      setLoading(false);
    }
  }, [teamId, navigate]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/team-pulse/teams/${teamId}`, { headers: authHeaders() });
        if (mounted) setData(r.data);
      } catch {
        if (mounted) {
          toast.error('Équipe introuvable.');
          navigate('/team-pulse/teams');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [teamId, navigate]);

  /* Scroll-spy sidebar */
  useEffect(() => {
    const onScroll = () => {
      for (const s of SECTIONS) {
        const el = document.getElementById(`pulse-section-${s.id}`);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.top >= -100 && r.top <= 260) { setActiveSection(s.id); break; }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(`pulse-section-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /* ---------- Handlers: regenerate / track (Section IV Rapports) ---------- */
  const handleRegenerate = useCallback(async (batch) => {
    if (!batch?.id || regeneratingId) return;
    setRegeneratingId(batch.id);
    const loadingToast = toast.loading('Génération du rapport enrichi en cours…');
    try {
      await axios.post(
        `${API}/team-pulse/batches/${batch.id}/generate-report`,
        {},
        { headers: authHeaders() }
      );
      toast.success(`Rapport v${batch.wave_number || ''} généré avec succès.`, { id: loadingToast });
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Impossible de générer le rapport.';
      toast.error(typeof msg === 'string' ? msg : 'Erreur inattendue lors de la génération.', { id: loadingToast });
    } finally {
      setRegeneratingId(null);
    }
  }, [regeneratingId, load]);

  const handleTrack = useCallback((batch) => {
    // 🆕 Iter388 — Bug "Suivre la progression" : ouvre la page de suivi
    // (TeamPulse phase=dashboard) pour la wave active. Avant Iter388 :
    // scroll silencieux vers la section add-member (action non explicite,
    // mauvaise UX).
    if (!batch?.id) return;
    try { localStorage.setItem('pulse_batch_id', batch.id); } catch (err) { /* noop */ }
    navigate(`/team-pulse?batch=${batch.id}`);
  }, [navigate]);

  /* ---------- Memo: derived team state (defensive, null-safe) ---------- */
  const derived = useMemo(() => {
    if (!data) return null;
    try {
      const { team, waves } = data;
      const wavesArr = Array.isArray(waves) ? waves : [];
      const currentWave = wavesArr.find((w) => w?.batch?.id === team?.current_wave_id)
        || wavesArr[wavesArr.length - 1];

      // 🆕 Iter388 — Bug 2 : la wave en cours peut ne PAS avoir de rapport
      // (ex: vague d'ajout de membre fraîchement créée). On expose donc la
      // dernière vague AVEC rapport ("latestReportedWave") pour préserver
      // l'accès au CTA "Présentation du dernier rapport" et aux données
      // d'enrichissement du roster (couleurs MBTI, dominant_color, etc.).
      const reportedWavesSorted = wavesArr
        .filter((w) => w?.batch?.report_unlocked && w?.batch?.report_data)
        .slice()
        .sort((a, b) => (b?.batch?.wave_number || 0) - (a?.batch?.wave_number || 0));
      const latestReportedWave = reportedWavesSorted[0] || null;
      const reportData = currentWave?.batch?.report_data || latestReportedWave?.batch?.report_data;

      // Flatten all invitations (for roster — tracks wave joined, null-safe)
      const rosterMap = new Map();
      for (const w of wavesArr) {
        const emails = w?.batch?.invited_emails || [];
        const pmap = w?.batch?.participants_map || {};
        for (const email of emails) {
          if (!email || typeof email !== 'string') continue;  // ignore null/non-string
          const key = email.toLowerCase();
          if (!rosterMap.has(key)) {
            rosterMap.set(key, {
              email: key,
              wave_number: w?.batch?.wave_number || 1,
              full_name: pmap[email] || pmap[key] || null,
            });
          }
        }
      }
      // 🆕 Iter388 — Bug 3 : enrichir le roster avec les couleurs/MBTI
      // historiques en parcourant TOUTES les waves ayant un rapport, du
      // plus récent au plus ancien. La 1ʳᵉ correspondance (la plus récente)
      // est conservée — ainsi les statuts ne disparaissent jamais après
      // l'ajout d'un nouveau membre (qui crée une wave sans rapport).
      for (const reportedWave of reportedWavesSorted) {
        const members = reportedWave?.batch?.report_data?.members;
        if (!Array.isArray(members)) continue;
        for (const m of members) {
          if (!m || typeof m.name !== 'string') continue;
          for (const r of rosterMap.values()) {
            if (r.dominant_color && r.mbti_code) continue;  // déjà enrichi par wave plus récente
            const base = (r.full_name && typeof r.full_name === 'string')
              ? r.full_name
              : (r.email ? r.email.split('@')[0] : '');
            if (!base) continue;
            const first = base.toLowerCase().split(' ')[0];
            if (first && m.name.toLowerCase().includes(first)) {
              if (!r.dominant_color) r.dominant_color = m.dominant_color;
              if (!r.mbti_code) r.mbti_code = m.mbti_code;
            }
          }
        }
      }
      const roster = Array.from(rosterMap.values());

      const completedCount = currentWave?.progress?.completed || 0;
      const totalCount = currentWave?.progress?.total
        || (currentWave?.batch?.invited_emails || []).length;
      const completionPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

      return {
        team, waves: wavesArr, currentWave, latestReportedWave, reportData, roster,
        completedCount, totalCount, completionPct,
      };
    } catch (err) {
      // Defensive : un crash dans le derive ne doit pas casser le render
      // eslint-disable-next-line no-console
      console.error('[PulseTeamDetail] derive error', err);
      return null;
    }
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F1EA] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#92710A]" />
      </div>
    );
  }
  if (!data || !derived) return null;

  const { team, waves, currentWave, latestReportedWave, reportData, roster, completedCount, totalCount, completionPct } = derived;

  const stance = reportData?.strategic_stance;
  const tldr = reportData?.executive_tldr;
  const distribution = reportData?.distribution;
  const teamDominantKey = reportData?.team_dominant || 'blue';
  const teamDominantColor = COLOR_TOKENS[teamDominantKey]?.hex || '#92710A';
  const balanceScore = reportData?.balance_score;
  const dnaOneliner = reportData?.dna?.oneliner;

  const openNewcomerWithSeed = () => {
    setAddMemberOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#F5F1EA] text-[#1F1A12]">
      {/* ============= HERO COVER-LIKE — Iter314 light premium ============= */}
      <section
        className="relative overflow-hidden"
        style={{ background: '#FAFAF8', color: '#1C1147' }}
        data-testid="pulse-team-hero"
      >
        {/* Halos ambient or + violet (cohérent Iter304-313) */}
        <span
          aria-hidden
          className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0) 70%)',
            filter: 'blur(40px)',
          }}
        />
        <span
          aria-hidden
          className="absolute top-[40%] -left-32 w-[420px] h-[420px] rounded-full pointer-events-none"
          style={{
            background: `radial-gradient(circle, ${teamDominantColor}22 0%, transparent 70%)`,
            filter: 'blur(40px)',
          }}
        />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-12 pt-10 pb-16">
          <BackButton variant="default" to="/dashboard" data-testid="pulse-team-detail-back" />

          <div className="flex items-center gap-2 mt-6 mb-2">
            <span className="w-6 h-px" style={{ background: 'linear-gradient(90deg, transparent, #D4AF37)' }} />
            <div className="text-[11px] tracking-[0.22em] uppercase font-semibold" style={{ color: '#B7901F' }}>
              CoachDIA · Pulse · Équipe
            </div>
          </div>

          {/* Top row: monogram + title + CTAs */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-5 mb-4">
                <Monogram name={team.name} color="#B7901F" size={76} ring="#D4AF37" />
                <div className="min-w-0">
                  <h1
                    className="font-serif text-3xl sm:text-4xl lg:text-[54px] leading-[1.1] break-words"
                    style={{
                      background: 'linear-gradient(135deg, #1C1147 0%, #3A2E6E 60%, #B7901F 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      fontWeight: 600,
                      letterSpacing: '-0.02em',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      wordBreak: 'break-word',
                      hyphens: 'auto',
                    }}
                    data-testid="pulse-team-detail-title"
                    title={team.name}
                  >
                    {team.name}
                  </h1>
                  <div className="mt-2 text-[13px] tabular-nums font-medium tracking-wide" style={{ color: '#5A5169' }}>
                    {(team.core_roster || []).length} MEMBRES · VAGUE {team.waves_count} ·
                    {` ${completionPct}% COMPLÉTION`}
                    {team.merged_from_teams?.length ? ` · CONSOLIDÉE` : ''}
                  </div>
                </div>
              </div>

              {/* Verdict strategic stance */}
              {stance?.verdict && (
                <div className="mt-6" data-testid="pulse-hero-verdict">
                  <div className="text-[10px] uppercase tracking-[0.30em] font-semibold mb-2" style={{ color: '#B7901F' }}>
                    Positionnement stratégique
                  </div>
                  <div className="font-serif text-xl lg:text-[24px] font-medium leading-snug max-w-3xl" style={{ color: '#1C1147' }}>
                    {stance.verdict}
                  </div>
                </div>
              )}

              {/* DNA oneliner */}
              {(dnaOneliner || tldr?.one_line_verdict) && (
                <p className="mt-4 italic font-light max-w-3xl text-[15px] leading-relaxed" style={{ color: '#5A5169' }} data-testid="pulse-hero-oneliner">
                  « {dnaOneliner || tldr?.one_line_verdict} »
                </p>
              )}
            </div>

            {/* CTAs */}
            <div className="flex flex-col gap-3 lg:min-w-[220px] shrink-0">
              {/* 🆕 Iter388 — Bug 2 : CTA "Présentation du Rapport" basé sur la
                  dernière vague AVEC rapport (latestReportedWave), pas sur la
                  current wave (qui peut être une vague d'ajout sans rapport). */}
              {latestReportedWave && (() => {
                const presentingBatch = latestReportedWave.batch;
                const isPaid = presentingBatch?.report_paid === true;
                const isCurrent = presentingBatch?.id === currentWave?.batch?.id;
                const handleBriefingClick = () => {
                  if (isPaid) {
                    navigate(`/team-pulse/teams/${team.id}/briefing?batch_id=${presentingBatch.id}`);
                  } else {
                    navigate(`/team-pulse?batch=${presentingBatch.id}&view=report`);
                  }
                };
                return (
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={handleBriefingClick}
                      aria-disabled={!isPaid}
                      title={isPaid ? 'Lancer la présentation vocale du rapport' : 'Présentation disponible après paiement du rapport'}
                      className={isPaid ? 'transition-all hover:-translate-y-0.5' : 'opacity-50 cursor-not-allowed'}
                      style={{
                        background: 'linear-gradient(135deg, #B7901F 0%, #D4AF37 50%, #F3D060 100%)',
                        color: '#1C1147',
                        padding: '12px 20px',
                        borderRadius: 999,
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        border: '1px solid rgba(255,255,255,0.4)',
                        boxShadow: '0 1px 0 rgba(255,255,255,0.4) inset, 0 12px 28px -10px rgba(212,175,55,0.55)',
                      }}
                      data-testid="pulse-team-briefing-btn"
                      data-paid={isPaid ? 'true' : 'false'}
                      data-wave={presentingBatch?.wave_number || 1}
                    >
                      <Mic className="w-4 h-4" />
                      {isCurrent ? 'Présentation du Rapport' : 'Présentation du dernier rapport'}
                      {!isPaid && <Lock className="w-3.5 h-3.5 ml-1" />}
                    </button>
                    {!isCurrent && (
                      <p
                        className="text-[11px] text-center"
                        style={{ color: '#6B5E48' }}
                        data-testid="pulse-team-briefing-wave-hint"
                      >
                        Vague {presentingBatch?.wave_number || 1}
                        {presentingBatch?.report_generated_at ? ` · ${new Date(presentingBatch.report_generated_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}
                      </p>
                    )}
                    {!isPaid && (
                      <p
                        className="text-[11px] text-center italic"
                        style={{ color: '#8A7A5E' }}
                        data-testid="pulse-team-briefing-locked-hint"
                      >
                        Disponible après paiement
                      </p>
                    )}
                  </div>
                );
              })()}
              <button
                onClick={() => setAddMemberOpen(true)}
                className="px-5 py-3 rounded-full font-medium transition flex items-center justify-center gap-2"
                style={{
                  background: '#FFFFFF',
                  color: '#1C1147',
                  border: '1.5px solid rgba(212,175,55,0.45)',
                  boxShadow: '0 6px 16px -10px rgba(28,17,71,0.18)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#FFFCEC'; e.currentTarget.style.borderColor = '#D4AF37'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.borderColor = 'rgba(212,175,55,0.45)'; }}
                data-testid="pulse-team-add-member-btn"
              >
                <UserPlus className="w-4 h-4" /> Ajouter un membre
              </button>
              <button
                onClick={() => setRefreshOpen(true)}
                className="px-5 py-3 rounded-full font-medium transition flex items-center justify-center gap-2"
                style={{
                  background: '#FFFFFF',
                  color: '#1C1147',
                  border: '1.5px solid rgba(212,175,55,0.45)',
                  boxShadow: '0 6px 16px -10px rgba(28,17,71,0.18)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#FFFCEC'; e.currentTarget.style.borderColor = '#D4AF37'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.borderColor = 'rgba(212,175,55,0.45)'; }}
                data-testid="pulse-team-new-wave-btn"
              >
                <Plus className="w-4 h-4" /> Nouvelle vague complète
              </button>
            </div>
          </div>

          {/* Bottom strip: balance + distribution + dominante */}
          {reportData && (
            <div
              className="mt-10 grid md:grid-cols-[auto_1fr_auto] gap-8 items-center pt-8"
              style={{ borderTop: '1px solid #E8E4D9' }}
              data-testid="pulse-hero-strip"
            >
              {/* Balance score */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: '#B7901F' }}>
                  Score d'équilibre
                </div>
                <div className="font-serif text-[40px] font-medium tabular-nums leading-none mt-1" style={{ color: '#1C1147' }}>
                  {balanceScore != null ? balanceScore.toFixed(1) : '—'}
                  <span className="text-xl" style={{ color: '#B7901F' }}>/100</span>
                </div>
              </div>

              {/* Distribution bars */}
              {distribution && (
                <div className="flex flex-col gap-2">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold mb-1" style={{ color: '#B7901F' }}>
                    Distribution des énergies
                  </div>
                  {Object.entries(COLOR_TOKENS).map(([k, v]) => {
                    const pct = distribution[k] || 0;
                    return (
                      <div key={k} className="flex items-center gap-3 text-[11px]" style={{ color: '#1C1147' }}>
                        <span className="w-20 uppercase tracking-wider font-semibold" style={{ color: v.hex }}>
                          {v.label}
                        </span>
                        <div className="flex-1 h-[6px] rounded-full overflow-hidden" style={{ background: 'rgba(28,17,71,0.06)' }}>
                          <div
                            className="h-full rounded-full transition-[width] duration-700"
                            style={{ width: `${Math.min(100, pct)}%`, background: v.hex, boxShadow: `0 0 8px ${v.hex}55` }}
                          />
                        </div>
                        <span className="w-12 text-right tabular-nums font-medium">{pct.toFixed(0)}%</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Dominante */}
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: '#B7901F' }}>
                  Dominante
                </div>
                <div className="font-serif text-2xl mt-1" style={{ color: teamDominantColor }}>
                  {COLOR_TOKENS[teamDominantKey]?.label}
                </div>
              </div>
            </div>
          )}

          <div className="text-[10px] tracking-[0.22em] uppercase mt-8 font-semibold" style={{ color: '#8A7A5E' }}>
            Mis à jour le {fmtDate(team.updated_at)}
          </div>
        </div>
      </section>

      {/* ============= MAIN + SIDEBAR ============= */}
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-12 grid lg:grid-cols-[240px_1fr] gap-10">
        {/* Sidebar sticky */}
        <aside className="hidden lg:block" data-testid="pulse-team-sidebar">
          <nav className="sticky top-6 space-y-1">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] font-semibold mb-3">
              Navigation
            </div>
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const active = activeSection === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition-all ${
                    active
                      ? 'font-semibold shadow-sm'
                      : 'text-[#6B5E48] hover:bg-[#EEE5D4]'
                  }`}
                  style={
                    active
                      ? {
                          background: 'linear-gradient(135deg, #FFFCEC 0%, #FFF4D2 100%)',
                          color: '#1C1147',
                          border: '1px solid rgba(212,175,55,0.45)',
                          boxShadow: '0 4px 12px -8px rgba(212,175,55,0.40)',
                        }
                      : undefined
                  }
                  data-testid={`pulse-section-nav-${s.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {s.label}
                </button>
              );
            })}
            {/* Signature éditoriale sidebar */}
            <div className="mt-8 pt-6 border-t border-[#E5DFD0]">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#8A7A5E] font-semibold mb-2">
                Outil exécutif
              </div>
              <p className="text-[11px] text-[#6B5E48] italic leading-snug">
                « Simulez un recrutement avant même de recruter. »
              </p>
            </div>
          </nav>
        </aside>

        {/* Main content */}
        <main className="space-y-14 min-w-0">
          {/* -------- Section I — Santé actuelle -------- */}
          <section id="pulse-section-health" data-testid="pulse-section-health" className="scroll-mt-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] font-semibold mb-2">Section I</div>
            <h2 className="font-serif text-3xl mb-5 border-b border-[#E5DFD0] pb-3">
              Santé actuelle
            </h2>

            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <KPICard label="Complétion" value={`${completionPct}%`} sub={`${completedCount} / ${totalCount} réponses`} />
              <KPICard label="Membres" value={roster.length} sub="Roster inclus historique" />
              <KPICard label="Vagues" value={team.waves_count} sub="Mesures réalisées" />
            </div>

            {/* TL;DR exécutif si dispo */}
            {tldr && (
              <div className="bg-white border border-[#E5DFD0] rounded-2xl p-6" data-testid="pulse-health-tldr">
                <div className="flex items-start gap-5">
                  <div className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center" style={{ background: '#F3D060' }}>
                    <Target className="w-5 h-5 text-[#1F1A12]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#92710A] font-semibold">
                      Lecture 30 secondes
                    </div>
                    <div className="font-serif text-lg mt-1 leading-snug">{tldr.one_line_verdict}</div>
                    {Array.isArray(tldr.three_takeaways) && tldr.three_takeaways.length > 0 && (
                      <ul className="mt-4 space-y-2">
                        {tldr.three_takeaways.slice(0, 3).map((t, i) => (
                          <li key={i} className="text-sm text-[#1F1A12] flex gap-3 leading-relaxed">
                            <span className="text-[#92710A] font-serif mt-0.5">{i + 1}.</span>
                            <span>{t}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Raccourci rapport si disponible — Iter293 PREMIUM CTA */}
            {currentWave?.batch?.report_paid && (
              <button
                onClick={() => navigate(`/team-pulse?batch=${currentWave.batch.id}&view=report`)}
                className="group mt-5 w-full flex items-center justify-between gap-4 bg-gradient-to-r from-[#F3D060] via-[#E8C040] to-[#C8A64B] hover:from-[#F5D572] hover:via-[#EFC850] hover:to-[#D4B25A] text-[#1F1A12] rounded-2xl p-5 shadow-[0_10px_30px_-8px_rgba(196,156,49,0.55)] hover:shadow-[0_14px_36px_-8px_rgba(196,156,49,0.75)] hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.99] transition-all duration-200 border border-[#C8A64B]/40"
                data-testid="pulse-health-open-report"
                aria-label="Ouvrir le rapport complet de cette vague"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center shadow-inner group-hover:scale-105 transition-transform"
                    style={{
                      background: 'linear-gradient(135deg, #B7901F 0%, #D4AF37 50%, #F3D060 100%)',
                      color: '#1C1147',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4), 0 6px 14px -8px rgba(212,175,55,0.55)',
                    }}>
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="text-left min-w-0">
                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#1F1A12]/70">Rapport débloqué</div>
                    <div className="text-base sm:text-lg font-semibold text-[#1F1A12] leading-tight">Ouvrir le rapport complet</div>
                  </div>
                </div>
                <div className="shrink-0 flex items-center gap-2 font-semibold text-sm">
                  <span className="hidden sm:inline">Lecture immersive</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
            )}
          </section>

          {/* -------- Section III — Gérer le roster (édition premium) -------- */}
          <section id="pulse-section-add-member" data-testid="pulse-section-add-member" className="scroll-mt-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] font-semibold mb-2">Section III</div>
            <h2 className="font-serif text-3xl mb-5 border-b border-[#E5DFD0] pb-3">
              Gérer le roster
            </h2>

            <RosterEditor
              teamId={team.id}
              rosterDetails={data.roster_details || []}
              onChange={load}
            />
          </section>

          {/* -------- Section IV — Rapports (PLIABLE — Iter293) -------- */}
          <CollapsibleSection
            id="reports"
            sectionLabel="Section IV"
            title="Rapports"
            defaultOpen={false}
            dataTestId="pulse-section-reports"
          >
            <ReportsSection
              waves={waves}
              currentWaveId={team.current_wave_id}
              onOpen={(batch) => navigate(`/team-pulse?batch=${batch.id}&view=report`)}
              onCompare={(currentBatch, baselineBatch) => setCompareOpen({ currentBatch, baselineBatch })}
              onRegenerate={handleRegenerate}
              onTrack={handleTrack}
              regeneratingId={regeneratingId}
            />
          </CollapsibleSection>

          {/* -------- Section V — Évolution -------- */}
          <section id="pulse-section-evolution" data-testid="pulse-section-evolution" className="scroll-mt-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] font-semibold mb-2">Section V</div>
            <h2 className="font-serif text-3xl mb-5 border-b border-[#E5DFD0] pb-3">
              Évolution dans le temps
            </h2>
            <WavesTimeline waves={waves} teamDominantColor={teamDominantColor} />
          </section>

          {/* -------- Section VI — Historique (PLIABLE — Iter293) -------- */}
          <CollapsibleSection
            id="history"
            sectionLabel="Section VI"
            title="Historique des vagues"
            defaultOpen={false}
            dataTestId="pulse-section-history"
          >
            <div className="divide-y divide-[#E5DFD0] bg-white border border-[#E5DFD0] rounded-2xl overflow-hidden">
              {waves.map((w) => (
                <div key={w.batch.id} className="flex items-center justify-between p-4 hover:bg-[#FAF5EA] transition-colors" data-testid={`pulse-history-row-${w.batch.wave_number}`}>
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="shrink-0 w-10 h-10 rounded-full bg-[#F3D060]/15 flex items-center justify-center font-serif text-[#92710A] text-sm font-semibold tabular-nums">
                      V{w.batch.wave_number}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{w.batch.name}</div>
                      <div className="text-xs text-[#6B5E48] mt-0.5">
                        {w.batch.wave_type} · {fmtDate(w.batch.created_at)}
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-[#92710A] font-semibold">
                      {w.batch.status}
                    </div>
                    <div className="text-xs text-[#6B5E48] mt-0.5 tabular-nums">
                      {w.invitations_count} invitations
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleSection>

          {/* Colophon footer */}
          <footer className="pt-8 border-t border-[#E5DFD0] text-center text-[10px] text-[#8A7A5E] tracking-[0.35em] uppercase">
            COACHDIA · PULSE · {team.name} · Mis à jour le {fmtDate(team.updated_at)}
          </footer>
        </main>
      </div>

      <AddMemberFlow
        open={addMemberOpen}
        onClose={() => setAddMemberOpen(false)}
        teamId={teamId}
        teamName={team.name}
        onReportGenerated={() => { setAddMemberOpen(false); load(); }}
      />
      <RefreshWaveFlow
        open={refreshOpen}
        onClose={() => setRefreshOpen(false)}
        teamId={teamId}
        teamName={team.name}
        coreRoster={team.core_roster || []}
        onReportGenerated={() => { setRefreshOpen(false); load(); }}
      />
      <CompareReportsModal
        open={!!compareOpen}
        onClose={() => setCompareOpen(null)}
        currentBatch={compareOpen?.currentBatch}
        baselineBatch={compareOpen?.baselineBatch}
      />
    </div>
  );
}

/* =============================================================================
 * Sub-components
 * =============================================================================*/
const KPICard = ({ label, value, sub }) => (
  <div className="bg-white border border-[#E5DFD0] rounded-xl p-5">
    <div className="text-[10px] uppercase tracking-[0.25em] text-[#92710A] font-semibold mb-2">{label}</div>
    <div className="font-serif text-4xl tabular-nums">{value}</div>
    <div className="text-xs text-[#6B5E48] mt-1">{sub}</div>
  </div>
);

/* ---------- Add member CTA (central conversion lever — ZÉRO simulation) ---------- */
const AddMemberCTA = ({ teamName, onOpen }) => (
  <div className="grid lg:grid-cols-[1.15fr_1fr] gap-0 bg-white border border-[#E5DFD0] rounded-2xl overflow-hidden shadow-[0_20px_50px_-30px_rgba(31,26,18,0.25)]" data-testid="pulse-add-member-cta">
    {/* Left: narrative positioning — mesure réelle, pas simulation */}
    <div className="p-7 lg:p-8 bg-gradient-to-br from-[#FAF5EA] to-[#F5F1EA]">
      <div className="flex items-center gap-2 mb-4">
        <UserPlus className="w-4 h-4 text-[#92710A]" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#92710A] font-semibold">
          Mesure réelle
        </span>
      </div>
      <h3 className="font-serif text-2xl mb-3 leading-snug text-[#1F1A12]">
        Ajoutez un membre,<br /> mesurez son impact réel.
      </h3>
      <p className="text-sm text-[#6B5E48] leading-relaxed mb-5">
        Invitez un nouveau membre à <span className="font-semibold">{teamName}</span>, il passera
        le même test que l'équipe. Une fois complété, vous générerez un <span className="italic">
        rapport v2 enrichi</span> — <span className="font-semibold">sans écraser l'existant</span>.
      </p>
      <div className="flex flex-wrap gap-2 text-[11px]">
        <span className="px-2.5 py-1 rounded-full font-semibold uppercase tracking-wider"
          style={{
            background: 'linear-gradient(135deg, #B7901F 0%, #D4AF37 50%, #F3D060 100%)',
            color: '#1C1147',
            boxShadow: '0 4px 10px -6px rgba(212,175,55,0.55)',
          }}
        >Test réel</span>
        <span className="px-2.5 py-1 bg-white border border-[#E5DFD0] text-[#6B5E48] rounded-full font-medium uppercase tracking-wider">Historique intact</span>
        <span className="px-2.5 py-1 bg-white border border-[#E5DFD0] text-[#6B5E48] rounded-full font-medium uppercase tracking-wider">Impact mesuré</span>
      </div>
    </div>

    {/* Right: CTA espresso — Iter314 light premium */}
    <div
      className="p-7 lg:p-8 flex flex-col justify-center items-stretch relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #FFFCEC 0%, #FFFFFF 55%, #F7F2E2 100%)',
        borderLeft: '1px solid rgba(212,175,55,0.30)',
      }}
    >
      <span aria-hidden className="absolute -top-16 -right-16 w-56 h-56 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.20) 0%, transparent 70%)', filter: 'blur(20px)' }} />
      <div className="text-[11px] uppercase tracking-[0.22em] font-semibold mb-3 relative" style={{ color: '#B7901F' }}>
        Workflow
      </div>
      <ol className="space-y-3 mb-6 text-sm relative" style={{ color: '#1C1147' }}>
        <li className="flex gap-3 items-start">
          <span className="w-6 h-6 rounded-full flex items-center justify-center font-serif text-xs shrink-0 mt-0.5"
            style={{
              background: 'linear-gradient(135deg, #B7901F, #F3D060)',
              color: '#FFFFFF',
              boxShadow: '0 4px 10px -6px rgba(212,175,55,0.55)',
            }}>1</span>
          <span>Vous invitez par email le nouveau membre</span>
        </li>
        <li className="flex gap-3 items-start">
          <span className="w-6 h-6 rounded-full flex items-center justify-center font-serif text-xs shrink-0 mt-0.5"
            style={{
              background: 'linear-gradient(135deg, #B7901F, #F3D060)',
              color: '#FFFFFF',
              boxShadow: '0 4px 10px -6px rgba(212,175,55,0.55)',
            }}>2</span>
          <span>Il réalise son test Pulse (lien magique, ~10 min)</span>
        </li>
        <li className="flex gap-3 items-start">
          <span className="w-6 h-6 rounded-full flex items-center justify-center font-serif text-xs shrink-0 mt-0.5"
            style={{
              background: 'linear-gradient(135deg, #B7901F, #F3D060)',
              color: '#FFFFFF',
              boxShadow: '0 4px 10px -6px rgba(212,175,55,0.55)',
            }}>3</span>
          <span>Vous générez le rapport v2 enrichi sur données réelles</span>
        </li>
      </ol>
      <button
        onClick={onOpen}
        className="px-6 py-3 rounded-full font-semibold transition flex items-center justify-center gap-2 relative"
        style={{
          background: 'linear-gradient(135deg, #B7901F 0%, #D4AF37 50%, #F3D060 100%)',
          color: '#1C1147',
          border: '1px solid rgba(255,255,255,0.4)',
          boxShadow: '0 1px 0 rgba(255,255,255,0.4) inset, 0 12px 28px -10px rgba(212,175,55,0.55)',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
        data-testid="pulse-add-member-cta-btn"
      >
        <UserPlus className="w-4 h-4" /> Ajouter un membre
      </button>
      <div className="text-[10px] italic text-center mt-3 relative" style={{ color: '#8A7A5E' }}>
        Suivi temps réel · Rapport historique préservé
      </div>
    </div>
  </div>
);

/* ---------- Reports cards decisional (Phase C, Iter270 + Iter272 statuts adaptatifs) ---------- */
const deriveLabel = (batch, isFirst) => {
  if (batch.report_version_label) return batch.report_version_label;
  const n = batch.wave_number || 1;
  const t = batch.wave_type || 'initial';
  if (n === 1 || isFirst) return `v${n} · Mesure initiale`;
  if (t === 'addition') return `v${n} · Équipe enrichie`;
  if (t === 'refresh') return `v${n} · Nouvelle vague (équipe complète)`;
  return `v${n} · Mesure`;
};

const MIN_COMPLETED = 2;  // seuil Pulse pour générer

const deriveWaveStatus = (wave, currentWaveId) => {
  const b = wave.batch;
  const progress = wave.progress || {};
  const completed = progress.completed || 0;
  const total = progress.total || (b.invited_emails || []).length;
  const hasReport = b.report_unlocked === true;
  const isActive = b.id === currentWaveId;

  if (hasReport) return isActive ? 'active' : 'historical';
  if (completed >= MIN_COMPLETED) return 'to_generate';
  if (b.status === 'active' && completed < total) return 'in_progress';
  return 'skip';
};

const STATUS_BADGE = {
  active:       { lbl: 'Actif',       bg: '#5B8A6B', color: 'white' },
  to_generate:  { lbl: 'À générer',   bg: '#F3D060', color: '#1F1A12' },
  in_progress:  { lbl: 'En cours',    bg: '#3B6B8F', color: 'white' },
  historical:   { lbl: 'Historique',  bg: '#8A7A5E', color: 'white' },
};

const ReportsSection = ({ waves, currentWaveId, onOpen, onCompare, onRegenerate, onTrack, regeneratingId }) => {
  const allWaves = (waves || []).slice()
    .sort((a, b) => (b.batch.wave_number || 0) - (a.batch.wave_number || 0));
  const visibleWaves = allWaves
    .map((w) => ({ wave: w, status: deriveWaveStatus(w, currentWaveId) }))
    .filter((x) => x.status !== 'skip');

  if (visibleWaves.length === 0) {
    return (
      <div className="bg-white border border-[#E5DFD0] rounded-2xl p-8 text-center" data-testid="pulse-reports-empty">
        <FileText className="w-10 h-10 text-[#92710A] mx-auto mb-3 opacity-60" />
        <p className="text-[#6B5E48] leading-relaxed max-w-lg mx-auto">
          Aucun rapport ni vague active. Lancez une première mesure pour voir apparaître
          vos rapports ici.
        </p>
      </div>
    );
  }

  const reportedWaveNums = visibleWaves
    .filter((x) => x.status === 'active' || x.status === 'historical')
    .map((x) => x.wave.batch.wave_number || 1);
  const firstReportedWaveNum = reportedWaveNums.length > 0 ? Math.min(...reportedWaveNums) : Infinity;

  return (
    <div className="space-y-3" data-testid="pulse-reports-list">
      {visibleWaves.map(({ wave, status }) => {
        const batch = wave.batch;
        const progress = wave.progress || {};
        const waveNum = batch.wave_number || 1;
        const isFirstReported = waveNum === firstReportedWaveNum;
        const badge = STATUS_BADGE[status];
        const baselineBatchId = batch.baseline_wave_id || batch.parent_batch_id;
        const baselineWave = waves.find((x) => x.batch.id === baselineBatchId);
        const canCompare = status !== 'in_progress' && status !== 'to_generate' && !isFirstReported && baselineWave;

        const bal = batch.report_data?.balance_score;
        const baselineBal = baselineWave?.batch?.report_data?.balance_score;
        const balDelta = (bal != null && baselineBal != null) ? bal - baselineBal : null;
        const members = batch.report_completed_count
          || progress.completed
          || (batch.invited_emails || []).length;
        const dom = batch.report_data?.team_dominant;
        const label = deriveLabel(batch, isFirstReported);

        return (
          <div
            key={batch.id}
            className={`bg-white border rounded-2xl p-5 transition-all ${
              status === 'active' ? 'border-[#92710A] shadow-[0_10px_30px_-15px_rgba(146,113,10,0.45)]'
              : status === 'to_generate' ? 'border-[#F3D060] shadow-[0_10px_30px_-15px_rgba(243,208,96,0.5)]'
              : 'border-[#E5DFD0] hover:border-[#92710A]/40'
            }`}
            data-testid={`pulse-report-card-${waveNum}`}
          >
            <div className="flex items-start gap-5">
              {/* Badge wave */}
              <div className="shrink-0 flex flex-col items-center">
                <div className="w-14 h-14 rounded-full flex items-center justify-center font-serif text-lg font-semibold text-white tabular-nums"
                     style={{ background: dom ? COLOR_TOKENS[dom]?.hex : (status === 'to_generate' ? '#F3D060' : '#92710A') }}>
                  V{waveNum}
                </div>
                <span className="mt-2 inline-flex items-center text-[9px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: badge.bg, color: badge.color }}
                      data-testid={`pulse-report-status-${waveNum}`}>
                  {badge.lbl}
                </span>
              </div>

              {/* Main */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-serif text-xl text-[#1F1A12] leading-tight">{label}</div>
                    <div className="text-xs text-[#6B5E48] mt-1 tabular-nums">
                      {fmtDate(batch.report_generated_at || batch.created_at)} · {members} membre{members > 1 ? 's' : ''}
                      {status === 'in_progress' && (
                        <span className="ml-2 text-[#3B6B8F] font-semibold">
                          · {progress.completed || 0}/{progress.total || (batch.invited_emails || []).length} complété{(progress.completed || 0) > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* KPIs inline — uniquement si rapport existe */}
                  {(status === 'active' || status === 'historical') && (
                    <div className="flex items-center gap-5 shrink-0">
                      {bal != null && (
                        <div className="text-right">
                          <div className="text-[9px] uppercase tracking-wider text-[#92710A] font-semibold">Équilibre</div>
                          <div className="font-serif text-xl tabular-nums text-[#1F1A12]">
                            {bal.toFixed(1)}
                            {balDelta != null && (
                              <span className={`text-xs font-semibold ml-1 ${
                                balDelta > 0 ? 'text-[#5B8A6B]' : balDelta < 0 ? 'text-[#B94A48]' : 'text-[#8A7A5E]'
                              }`}>
                                ({balDelta > 0 ? '+' : ''}{balDelta.toFixed(1)})
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      {dom && (
                        <div className="text-right">
                          <div className="text-[9px] uppercase tracking-wider text-[#92710A] font-semibold">Dominante</div>
                          <div className="font-serif text-base" style={{ color: COLOR_TOKENS[dom]?.hex }}>
                            {COLOR_TOKENS[dom]?.label || dom}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* CTAs adaptatifs selon statut */}
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-[#E5DFD0]">
                  {status === 'to_generate' && (
                    <button
                      onClick={() => onRegenerate(batch)}
                      disabled={regeneratingId === batch.id}
                      className="inline-flex items-center gap-1.5 bg-[#F3D060] text-[#1F1A12] px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#E8C040] transition shadow-[0_8px_25px_-10px_rgba(243,208,96,0.7)] disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid={`pulse-report-regenerate-${waveNum}`}
                    >
                      {regeneratingId === batch.id ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" /> Génération…
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" /> Régénérer le rapport enrichi
                        </>
                      )}
                    </button>
                  )}
                  {status === 'in_progress' && (
                    <button
                      onClick={() => onTrack(batch)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition"
                      style={{
                        background: 'linear-gradient(135deg, #B7901F 0%, #D4AF37 50%, #F3D060 100%)',
                        color: '#1C1147',
                        border: '1px solid rgba(255,255,255,0.4)',
                        boxShadow: '0 1px 0 rgba(255,255,255,0.4) inset, 0 8px 18px -8px rgba(212,175,55,0.5)',
                      }}
                      data-testid={`pulse-report-track-${waveNum}`}
                    >
                      <Eye className="w-3.5 h-3.5" /> Suivre la progression
                    </button>
                  )}
                  {(status === 'active' || status === 'historical') && (
                    <>
                      <button
                        onClick={() => onOpen(batch)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition"
                      style={{
                        background: 'linear-gradient(135deg, #B7901F 0%, #D4AF37 50%, #F3D060 100%)',
                        color: '#1C1147',
                        border: '1px solid rgba(255,255,255,0.4)',
                        boxShadow: '0 1px 0 rgba(255,255,255,0.4) inset, 0 8px 18px -8px rgba(212,175,55,0.5)',
                      }}
                        data-testid={`pulse-report-open-${waveNum}`}
                      >
                        <ArrowRight className="w-3.5 h-3.5" /> Voir le rapport
                      </button>
                      {batch.report_paid ? (
                        <button
                          onClick={() => downloadPdfFromBatch(batch)}
                          className="inline-flex items-center gap-1.5 bg-white border border-[#6B5E48]/30 text-[#1F1A12] px-4 py-2 rounded-full text-xs font-medium hover:bg-[#FAF5EA] transition"
                          data-testid={`pulse-report-pdf-${waveNum}`}
                        >
                          <Download className="w-3.5 h-3.5" /> Télécharger PDF
                        </button>
                      ) : (
                        <button
                          onClick={() => onOpen(batch)}
                          className="inline-flex items-center gap-1.5 bg-white border border-[#6B5E48]/30 text-[#6B5E48] px-4 py-2 rounded-full text-xs font-medium hover:bg-[#FAF5EA] transition italic"
                          title="Le PDF premium nécessite l'achat du rapport"
                          data-testid={`pulse-report-unlock-pdf-${waveNum}`}
                        >
                          <Download className="w-3.5 h-3.5" /> Débloquer PDF
                        </button>
                      )}
                      {canCompare && (
                        <button
                          onClick={() => onCompare(batch, baselineWave.batch)}
                          className="inline-flex items-center gap-1.5 bg-[#F3D060]/15 border border-[#F3D060] text-[#92710A] px-4 py-2 rounded-full text-xs font-semibold hover:bg-[#F3D060]/25 transition"
                          data-testid={`pulse-report-compare-${waveNum}`}
                        >
                          <GitCompare className="w-3.5 h-3.5" /> Comparer à v{baselineWave.batch.wave_number}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

async function downloadPdfFromBatch(batch) {
  try {
    const r = await fetch(`${API}/team-pulse/batches/${batch.id}/report/pdf`, {
      headers: authHeaders(),
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${batch.name || 'rapport-pulse'}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error('Téléchargement du PDF impossible (paiement requis ou accès refusé).');
  }
}

/* ---------- Waves Timeline (SVG premium) ---------- */
const WavesTimeline = ({ waves, teamDominantColor }) => {
  // Filtre : seulement waves avec rapport réellement généré (cohérent section Rapports)
  const reported = (waves || []).filter((w) => w.batch.report_unlocked && w.batch.report_data);
  if (reported.length === 0) return null;

  if (reported.length === 1) {
    return (
      <div className="bg-white border border-[#E5DFD0] rounded-2xl p-8 text-center" data-testid="pulse-evolution-single">
        <TrendingUp className="w-10 h-10 text-[#92710A] mx-auto mb-3 opacity-60" />
        <p className="text-[#6B5E48] leading-relaxed max-w-lg mx-auto">
          Une seule vague mesurée à ce jour. <span className="italic">Lancez une deuxième vague</span> pour
          voir apparaître la courbe d'évolution de votre équipe.
        </p>
      </div>
    );
  }

  // Multi-wave: SVG horizontal timeline with balance_score trajectory
  const points = reported.map((w) => ({
    wave: w.batch.wave_number || 1,
    date: w.batch.created_at,
    balance: w.batch?.report_data?.balance_score ?? null,
    name: w.batch.name,
    type: w.batch.wave_type,
    dominant: w.batch?.report_data?.team_dominant,
  }));

  const width = 800;
  const height = 220;
  const pad = { t: 30, b: 50, l: 40, r: 40 };
  const innerW = width - pad.l - pad.r;
  const n = points.length;
  const xOf = (i) => pad.l + (innerW * i) / Math.max(1, n - 1);
  const balances = points.map((p) => p.balance).filter((v) => v != null);
  const hasAnyBalance = balances.length > 0;
  const yMax = hasAnyBalance ? Math.max(100, ...balances) : 100;
  const yMin = hasAnyBalance ? Math.min(50, ...balances) : 50;
  const yOf = (v) => pad.t + (height - pad.t - pad.b) * (1 - (v - yMin) / Math.max(1, yMax - yMin));

  const pathD = hasAnyBalance
    ? points
        .map((p, i) => (p.balance == null ? null : `${i === 0 ? 'M' : 'L'} ${xOf(i)} ${yOf(p.balance)}`))
        .filter(Boolean)
        .join(' ')
    : '';

  return (
    <div className="bg-white border border-[#E5DFD0] rounded-2xl p-6" data-testid="pulse-evolution-timeline">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#92710A] font-semibold">
          Trajectoire du score d'équilibre
        </div>
        {hasAnyBalance && (
          <div className="text-xs text-[#6B5E48] tabular-nums">
            Min {yMin.toFixed(0)} · Max {yMax.toFixed(0)}
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full min-w-[500px]" data-testid="pulse-evolution-svg">
          {/* Gridline */}
          <line x1={pad.l} y1={height - pad.b} x2={width - pad.r} y2={height - pad.b} stroke="#E5DFD0" strokeWidth="1" />

          {/* Area under curve */}
          {hasAnyBalance && (
            <>
              <path
                d={`${pathD} L ${xOf(n - 1)} ${height - pad.b} L ${xOf(0)} ${height - pad.b} Z`}
                fill={teamDominantColor}
                opacity="0.08"
              />
              <path d={pathD} fill="none" stroke={teamDominantColor} strokeWidth="2.5" strokeLinecap="round" />
            </>
          )}

          {/* Points + labels */}
          {points.map((p, i) => {
            const cx = xOf(i);
            const cy = p.balance == null ? height - pad.b : yOf(p.balance);
            const domColor = COLOR_TOKENS[p.dominant]?.hex || '#92710A';
            return (
              <g key={i}>
                <circle cx={cx} cy={cy} r="6" fill={domColor} stroke="#fff" strokeWidth="3" />
                {p.balance != null && (
                  <text x={cx} y={cy - 12} textAnchor="middle" fontSize="11"
                        fill="#1F1A12" fontWeight="600" fontFamily="Georgia">
                    {p.balance.toFixed(1)}
                  </text>
                )}
                <text x={cx} y={height - pad.b + 18} textAnchor="middle" fontSize="10"
                      fill="#6B5E48" fontWeight="600" letterSpacing="1">
                  V{p.wave}
                </text>
                <text x={cx} y={height - pad.b + 32} textAnchor="middle" fontSize="9"
                      fill="#8A7A5E">
                  {fmtShort(p.date)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Wave cards list below timeline */}
      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        {points.map((p, i) => (
          <div key={i} className="flex items-center gap-3 p-3 bg-[#FAF5EA] rounded-lg" data-testid={`pulse-evolution-wave-${p.wave}`}>
            <div className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white font-serif text-xs font-semibold"
                 style={{ background: COLOR_TOKENS[p.dominant]?.hex || '#92710A' }}>
              V{p.wave}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate text-[#1F1A12]">{p.name}</div>
              <div className="text-[11px] text-[#6B5E48] tabular-nums">
                {fmtDate(p.date)} · {p.type}
                {p.balance != null ? ` · ${p.balance.toFixed(1)}/100` : ''}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PulseTeamsList;
